import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './auth/components/auth.guard';
import { UserResolver } from './shared/resolvers/user.resolver';
import { AdminGuard } from './shared/guards/admin.guard';
import { ListCategoriesComponent } from './categories/components/list-categories/list-categories.component';
import { hasPermission } from './shared/guards/permission.guard';
import { PERMISSIONS } from '../../../common/enums';
import { rootRedirectGuard } from './shared/guards/root-redirect.guard';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { LoginComponent } from './auth/components/login/login.component';
import { RegisterComponent } from './auth/components/register/register.component';
import { ForgotPasswordComponent } from './auth/components/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './auth/components/reset-password/reset-password.component';

const routes: Routes = [
    // Root redirect
    {
        path: '',
        pathMatch: 'full',
        canActivate: [rootRedirectGuard],
        children: [],
    },
    // Auth routes - use AuthLayoutComponent (no sidebar)
    {
        path: 'auth',
        component: AuthLayoutComponent,
        children: [
            { path: '', redirectTo: 'login', pathMatch: 'full' },
            { path: 'login', component: LoginComponent },
            { path: 'register', component: RegisterComponent },
            { path: 'forgot-password', component: ForgotPasswordComponent },
            { path: 'reset-password', component: ResetPasswordComponent },
        ],
    },
    // Main app routes - use MainLayoutComponent (with sidebar)
    {
        path: '',
        component: MainLayoutComponent,
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
                resolve: {
                    userData: UserResolver,
                },
            },
            {
                path: 'customers',
                loadChildren: () => import('./customers/customers.module').then((m) => m.CustomersModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'categories',
                children: [
                    {
                        path: '',
                        component: ListCategoriesComponent,
                        canActivate: [() => hasPermission(PERMISSIONS.PRODUCT_READ)],
                    },
                ],
            },
            {
                path: 'customer-requests',
                loadChildren: () =>
                    import('./customer-requests/customer-requests.module').then((m) => m.CustomerRequestsModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'expenses',
                loadChildren: () => import('./expenses/expenses.module').then((m) => m.ExpensesModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'quotes',
                loadChildren: () => import('./quotes/quotes.module').then((m) => m.QuotesModule),
            },
            {
                path: 'reports',
                loadChildren: () => import('./reports/reports.module').then((m) => m.ReportsModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'suppliers',
                loadChildren: () => import('./suppliers/suppliers.module').then((m) => m.SuppliersModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'purchase-orders',
                loadChildren: () => import('./purchase-order/purchase-order.module').then((m) => m.PurchaseOrderModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'stock-returns',
                loadChildren: () => import('./stock-returns/stock-returns.module').then((m) => m.StockReturnsModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'supplier-bills',
                loadChildren: () => import('./supplier-bills/supplier-bills.module').then((m) => m.SupplierBillsModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'supplier-credit-notes',
                loadChildren: () =>
                    import('./supplier-credit-notes/supplier-credit-notes.module').then(
                        (m) => m.SupplierCreditNotesModule,
                    ),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'supplier-payments',
                loadChildren: () =>
                    import('./supplier-payments/supplier-payments.module').then((m) => m.SupplierPaymentsModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'customer-payments',
                loadChildren: () =>
                    import('./customer-payments/customer-payments.module').then((m) => m.CustomerPaymentsModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'pos',
                loadChildren: () => import('./pos/pos.module').then((m) => m.PosModule),
            },
            {
                path: 'products',
                loadChildren: () => import('./products/products.module').then((m) => m.ProductsModule),
            },
            {
                path: 'settings',
                loadChildren: () => import('./settings/settings.module').then((m) => m.SettingsModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'users',
                loadChildren: () => import('./users/users.module').then((m) => m.UsersModule),
                canActivate: [() => inject(AdminGuard).canActivate()],
            },
            {
                path: 'invoices',
                loadChildren: () => import('./invoices/invoices.module').then((m) => m.InvoicesModule),
            },
        ],
        canActivate: [() => inject(AuthGuard).canActivate()],
    },
    { path: '**', redirectTo: '' },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { bindToComponentInputs: true })],
    exports: [RouterModule],
})
export class AppRoutingModule {}
